/**
 * THE port. The React layer codes against this interface and never learns
 * whether a backend exists (plan §1.1 · architecture §0).
 *
 * Five rules keep that real rather than aspirational, and three of them are
 * visible in this file:
 *
 *  1. Every method returns `Promise<T>` EVEN IN LOCAL MODE. A synchronous
 *     `LocalRepository.getDashboard()` would let a component infer the adapter
 *     from its own call site, and the inference would be correct — which is
 *     what makes it fatal. `LocalRepository` therefore wraps pure, already
 *     -computed values in resolved promises on purpose.
 *  2. Every `T` is inferred from a Zod schema in `@meridian/contracts`, so local
 *     and HTTP return the SAME TypeScript symbol rather than two similar ones.
 *     That is why nothing in this file declares a payload shape of its own.
 *  3. Adapter selection happens once at boot from a Zod-validated
 *     `VITE_DATA_SOURCE`; production has no default (plan §1.1 rule 4).
 *
 * The two implementations are `LocalRepository` (this package,
 * `src/adapters/`) and `HttpRepository` (`apps/web/src/data/`) — never
 * `ApiRepository`: name the TRANSPORT, never the backend, or the one symbol in
 * a package forbidden to know an API exists would be named after one
 * (naming §3.5).
 *
 * Query parameters use the API vocabulary (`window`, `index`), not the URL
 * vocabulary (`win`, `idx`). The two are deliberately different and the mapping
 * happens once, in the web query hooks (ADR-0003 · plan §6.6 traps).
 */
import type {
  AreaDetail,
  AreaId,
  AreaListPayload,
  AreaQuery,
  BenchmarkOhlcQuery,
  BenchmarkOhlcResult,
  BookPayload,
  DashboardPayload,
  DashboardQuery,
  DatasetMeta,
  MarketSnapshotPayload,
  DiversificationPayload,
  OhlcSeries,
  PeersPayload,
  PriceSeries,
  SearchQuery,
  SectorDetail,
  SectorId,
  SectorQuery,
  Security,
  SecurityProfile,
  SeriesQuery,
  Ticker,
} from '@meridian/contracts';

/**
 * Raised when an id does not resolve. Both adapters reject with this rather
 * than resolving an empty payload, so `apps/api` can map it to a 404 and the
 * SPA can render "not found" instead of an empty screen (plan §6.6: unknown
 * ticker/sector → 404, never an empty 200).
 */
/** Closed vocabulary for the resource a missing id refers to. */
export type DatasetResource = 'security' | 'sector' | 'area';

export class DatasetNotFoundError extends Error {
  /** Discriminant, so a catch site does not depend on `instanceof` surviving a bundler. */
  public readonly kind = 'dataset-not-found' as const;

  public constructor(
    public readonly resource: DatasetResource,
    public readonly id: string,
  ) {
    super(`${resource} not found: ${id}`);
    this.name = 'DatasetNotFoundError';
  }
}

/**
 * Realm-safe guard: require the discriminant AND resource/id strings so a bare
 * `{ kind: 'dataset-not-found' }` does not type-narrow to a full error with
 * undefined fields at the HTTP 404 mapping site.
 */
export function isDatasetNotFoundError(error: unknown): error is DatasetNotFoundError {
  if (typeof error !== 'object' || error === null) return false;
  const e = error as { kind?: unknown; resource?: unknown; id?: unknown };
  return (
    e.kind === 'dataset-not-found' && typeof e.resource === 'string' && typeof e.id === 'string'
  );
}

/** Closed vocabulary for the port method that could not produce an OHLC result. */
export type OhlcUnavailableMethod = 'getOhlcSeries' | 'getBenchmarkOhlcSeries';

/**
 * Raised when an adapter cannot even produce the OHLC union (ADR-0045).
 * Expected gaps stay `{ status: 'unavailable', reason, coverage }` — this
 * throw is transport/parse/unimplemented, analogous to `DatasetNotFoundError`.
 */
export class OhlcUnavailableError extends Error {
  /** Discriminant, so a catch site does not depend on `instanceof` surviving a bundler. */
  public readonly kind = 'ohlc-unavailable' as const;

  public constructor(public readonly method: OhlcUnavailableMethod) {
    super(`${method} cannot produce an OHLC result`);
    this.name = 'OhlcUnavailableError';
  }
}

/**
 * Realm-safe guard: require the discriminant AND a closed method so a bare
 * `{ kind: 'ohlc-unavailable' }` does not type-narrow with an undefined method.
 */
export function isOhlcUnavailableError(error: unknown): error is OhlcUnavailableError {
  if (typeof error !== 'object' || error === null) return false;
  const e = error as { kind?: unknown; method?: unknown };
  return (
    e.kind === 'ohlc-unavailable' &&
    (e.method === 'getOhlcSeries' || e.method === 'getBenchmarkOhlcSeries')
  );
}

export interface DatasetRepository {
  /** Freshness oracle. Local mode reports `synthetic` with null ingest state. */
  readonly getMeta: () => Promise<DatasetMeta>;

  /** Composite dashboard payload — rows, sector aggregates and breadth in one. */
  readonly getDashboard: (query: DashboardQuery) => Promise<DashboardPayload>;

  /**
   * The manual book — which rows are the operator's (ADR-0042 · #84).
   *
   * A whole-book payload rather than a per-ticker lookup, and that is what lets
   * the stock screen mark an owned name with a client-side join instead of
   * putting `qty`/`cost` on `SecurityProfile`. Independent of `window`/`index`:
   * ownership is not a function of either.
   *
   * Local mode serves a synthetic fixture; live mode serves the operator CSV.
   * Neither ever rejects for an EMPTY book — an unwritten file is an empty
   * book, not an error, and a 500 here would take down every screen.
   */
  readonly getBook: () => Promise<BookPayload>;

  /**
   * Vendor last/prev-close quotes for the dashboard hero (ADR-0032).
   * Independent of `window`/`index`. Missing series are omitted, never zeroed.
   */
  readonly getMarketSnapshot: () => Promise<MarketSnapshotPayload>;

  /** Sector screen: KPIs, members, movers, correlation matrix, diversifiers. */
  readonly getSector: (id: SectorId, query: SectorQuery) => Promise<SectorDetail>;

  /** Industry-area list for one taxonomy (v1: industry only). */
  readonly getAreas: (query: AreaQuery) => Promise<AreaListPayload>;

  /** Area screen: KPIs, members, movers, optional matrix, diversifiers. */
  readonly getArea: (id: AreaId, query: AreaQuery) => Promise<AreaDetail>;

  /**
   * Stock screen: row, 52w stats, `consensus | null`, `catalysts[]`,
   * `financials | null`. `query.window` selects the active-window `vol`/`beta`
   * join (ADR-0022). 52-week stats stay the 1Y series regardless.
   */
  readonly getSecurity: (ticker: Ticker, query: SeriesQuery) => Promise<SecurityProfile>;

  /** Complete bar set for the window — decimation is the chart's business. */
  readonly getSeries: (ticker: Ticker, query: SeriesQuery) => Promise<PriceSeries>;

  /**
   * SPY adjusted-close window (issue #82 · ADR-0043). Same `PriceSeries`
   * shape as `getSeries`. Not a universe member: a missing calendar is a
   * server fault on the HTTP adapter, never a `DatasetNotFoundError`.
   */
  readonly getBenchmarkSeries: (query: SeriesQuery) => Promise<PriceSeries>;

  /**
   * Raw OHLCV window for a universe ticker (ADR-0045). Local mode is a
   * complete envelope over `generateSeries` — never a warmup rejection.
   */
  readonly getOhlcSeries: (ticker: Ticker, query: SeriesQuery) => Promise<OhlcSeries>;

  /**
   * Benchmark OHLCV union (ADR-0045). Local 3Y is always `available` for
   * both SPY and QQQ. The unavailable arm is an empty complete suffix
   * (live-shaped opt-in / fixture override). An adapter that cannot produce
   * the union at all throws `OhlcUnavailableError`.
   */
  readonly getBenchmarkOhlcSeries: (query: BenchmarkOhlcQuery) => Promise<BenchmarkOhlcResult>;

  /** Same-sector and same-industry leaders by return; the subject appears only if it ranks. */
  readonly getPeers: (ticker: Ticker, query: SeriesQuery) => Promise<PeersPayload>;

  /** Three mutually exclusive groups plus the correlation extremes. */
  readonly getDiversifiers: (ticker: Ticker, query: SeriesQuery) => Promise<DiversificationPayload>;

  /**
   * The ONLY typeahead path in both modes. `HttpRepository` prefetches the
   * universe once per `asOf` and delegates to the same `core` selector, so the
   * §6.6 ordering contract is provable by shared code rather than only tested
   * by parity fixtures.
   */
  readonly search: (query: SearchQuery) => Promise<readonly Security[]>;
}
